import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ClipboardPlus, CalendarX2, ArrowLeftRight, MoreHorizontal, ChevronDown, Clock, Image as ImageIcon, ClipboardList, ReceiptText, Warehouse, Briefcase, AlertTriangle } from "lucide-react";
import { api, apiError, uploadFiles } from "@/lib/api";
import { storage } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import { effectiveAttendanceMode, correctedMode } from "@/lib/attendanceMode";
import MyShiftBlock from "@/components/MyShiftBlock";
import MyAdvancesCard from "@/components/cashbox/MyAdvancesCard";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { AbsenceDialog } from "@/components/absences/AbsenceDialog";
import { usePhotoInputs } from "@/components/shared/PhotoPicker";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import SitePicker from "@/components/mobile/SitePicker";
import ShiftEndFixDialog, { needsEndFix } from "@/components/shared/ShiftEndFixDialog";
import { Running, Bar } from "@/components/mobile/ManagerHome";
import { shiftStateFrom, hoursLabel } from "@/components/mobile/shiftState";
import { pragueMin, dayLabel } from "@/components/mobile/todayFeed";

// Úvod dělníka na telefonu (199, návrh B — docházka na stavbu). Počítač beze změny.
//   karta směny: stavba se vybere předem (klepnutím) a podržení tlačítka dole
//   přihlásí rovnou na ni; v práci běží čas
//   4 rychlé akce: fotka ze stavby, nový úkol, volno · lékař / jiná stavba, další
//   pod tím stávající úkoly s tahy prstem, hodiny a zálohy (MyShiftBlock bez karty)
// 202: stejný úvod má subdodavatel (místo fotky „Nahrát fakturu“ — fotky do banky
//   zakázky nesmí) a skladník (místo fotky „Sklad“).

const TZ = "Europe/Prague";
const MAX_PHOTOS = 10;
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: TZ });
const fotky = (n) => (n === 1 ? "fotka" : n >= 2 && n <= 4 ? "fotky" : "fotek");
const czDay = (iso) => new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", timeZone: TZ }).format(new Date(iso));

function QuickAction({ icon: Icon, label, onClick, badge, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="flex flex-col items-center gap-[7px] min-w-0 pt-0.5 pb-1 active:scale-95 transition-transform duration-200 motion-reduce:transition-none">
      <span className="relative w-14 h-14 rounded-full bg-card grid place-items-center shadow-[0_1px_2px_rgba(15,15,16,.06),inset_0_0_0_1px_hsl(var(--border))]">
        <Icon size={24} aria-hidden="true" />
        {badge ? <span className="absolute -top-[3px] -right-[5px] min-w-5 h-5 px-[5px] rounded-full bg-brand text-white text-[11px] font-bold grid place-items-center tabular-nums">{badge}</span> : null}
      </span>
      <span className="text-[12.5px] font-semibold leading-tight text-center max-w-[86px]">{label}</span>
    </button>
  );
}

export default function WorkerHome() {
  const { user, company } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = user?.role;
  const key = `pp-site-${user?.id}`;
  // 219: režim docházky z přihlášení hned (dřív až po /daysheet/current — karta směny
  // a její data startovala o jedno kolo později); /me/overview ho případně opraví.
  const [mode, setMode] = useState(() => effectiveAttendanceMode(user, company));
  const [sh, setSh] = useState(null);
  const [site, setSite] = useState(() => storage.get(key) || null);
  const [pickOpen, setPickOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [absOpen, setAbsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [photosToday, setPhotosToday] = useState(0);
  const [uploading, setUploading] = useState(false);
  // 254: neodhlášené směny (tento a minulý měsíc) — doplní se v okně přímo tady
  const [fixRows, setFixRows] = useState([]);
  const [fixRow, setFixRow] = useState(null);
  const loadFix = useCallback((want) => {
    const m0 = todayIso().slice(0, 7), d = new Date(`${m0}-01T12:00:00`); d.setMonth(d.getMonth() - 1);
    const m1 = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    Promise.all([m1, m0].map((m) => api.get(`/attendance/monthly?month=${m}`).then((r) => r.data.rows || []).catch(() => [])))
      .then(([a, b]) => {
        const all = [...a, ...b].filter(needsEndFix);
        setFixRows(all);
        const r = want && all.find((x) => x.id === want);
        if (r) setFixRow(r);
      });
  }, []);
  // odkaz z upozornění „Nebyl jste řádně odhlášen“ (/?konec=<směna>) okno rovnou otevře
  const konec = params.get("konec");
  useEffect(() => {
    loadFix(konec);
    if (konec) { const n = new URLSearchParams(params); n.delete("konec"); setParams(n, { replace: true }); }
  }, [konec]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onShiftState = useCallback((st) => {
    setSh(st);
    setMode((m) => correctedMode(m, st?.ov));
  }, []);
  // Layout po podržení tlačítka čte předvolenou stavbu z úložiště — držet ho v souladu
  useEffect(() => {
    const onStore = () => setSite(storage.get(key) || null);
    window.addEventListener("pp-shift-changed", onStore);
    return () => window.removeEventListener("pp-shift-changed", onStore);
  }, [key]);

  const ov = sh?.ov, pd = sh?.pd;
  const projects = ov?.my_projects || [];
  // jediná stavba = vybraná sama; neplatná (už není v týmu) = zapomenout
  useEffect(() => {
    if (!ov) return;
    const ids = projects.map((p) => p.id);
    if (site && !ids.includes(site)) { setSite(null); try { storage.set(key, ""); } catch { /* noop */ } }
    else if (!site && ids.length === 1) { setSite(ids[0]); try { storage.set(key, ids[0]); } catch { /* noop */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ov]);

  const st = shiftStateFrom(ov, { mode: mode || "site", day: sh?.day, todayHours: sh?.todayHours });
  const working = st.state === "working" && !!st.since;
  const daysheet = st.mode === "daysheet";
  const shiftPid = ov?.shift?.project_id || null;
  const sitePid = shiftPid || site;
  const siteName = projects.find((p) => p.id === sitePid)?.name || ov?.shift?.project_name || null;
  const week = Number(pd?.hours?.week) || 0;
  const last = pd?.shift?.lastShift;

  const chooseSite = (id) => {
    setSite(id); setPickOpen(false);
    try { storage.set(key, id); } catch { /* noop */ }
    const n = projects.find((p) => p.id === id)?.name;
    toast.success("Stavba vybraná", { description: `${n || ""} · podrž tlačítko dole a jsi v práci` });
  };

  // ---- fotka ze stavby: hned se nahraje do banky fotek zakázky (ke schválení do deníku) ----
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS);
    e.target.value = "";
    if (!files.length || !sitePid) return;
    setUploading(true);
    const id = toast.loading(`Nahrávám ${files.length} ${fotky(files.length)}…`, { description: "Nech aplikaci otevřenou, dokud se nenahrají" });
    try {
      // 257: nepovedená fotka (slabý signál) už nezahodí ostatní — uloží se, co prošlo
      const { urls, failed } = await uploadFiles(files, {
        onEach: (done, total) => toast.loading(`Nahrávám ${done} z ${total}…`, { id, description: "Nech aplikaci otevřenou, dokud se nenahrají" }),
      });
      if (!urls.length) throw failed[0]?.error || new Error("upload");
      await api.post(`/projects/${sitePid}/photos`, { urls, date: todayIso() });
      setPhotosToday((n) => n + urls.length);
      if (failed.length) toast.warning(`Uloženo ${urls.length} z ${files.length}`, { id, description: `${failed.length} ${fotky(failed.length)} neprošlo — ${apiError(failed[0].error)}` });
      else toast.success(`${urls.length} ${fotky(urls.length)} uloženo`, { id, description: `${siteName || "Stavba"} · uvidí je stavbyvedoucí` });
    } catch (err) {
      toast.error("Fotky se nepodařilo nahrát", { id, description: apiError(err) });
    } finally { setUploading(false); }
  };
  const photoPick = usePhotoInputs({ onChange: addPhotos, disabled: uploading });
  const photo = () => {
    if (!sitePid) { toast.info("Nejdřív vyber stavbu", { description: "Ke které stavbě fotka patří" }); setPickOpen(true); return; }
    photoPick.open();
  };

  // ---- karta směny ----
  // 258: nedoplněný konec neodhlášené směny patří do karty (červená) — bez něj se nepřihlásí,
  // takže místo výběru stavby je jediná akce Doplnit
  // 260: Příchod zavře jen směna, která opravdu blokuje (ne stará ani s výjimkou člena) — ostatní jsou připomínka pod kartou
  const fix = !working && !daysheet && st.state !== "draft" && st.state !== "ended" ? fixRows.find((r) => r.blocks_check_in !== false) || null : null;
  let big, sub, chip = null, onCard;
  if (fix) {
    chip = "Nedokončená směna"; big = "Doplň konec směny";
    sub = `Nebyl jsi odhlášen ${czDay(fix.check_in)} · ${fix.project}`;
    onCard = () => setFixRow(fix);
  } else if (working) {
    onCard = () => nav("/?shift=1");                      // mezikrok: jiná stavba / odchod
  } else if (st.state === "draft") {
    chip = "Výkaz k vyplnění"; big = "Vyplň výkaz"; sub = "Rozděl dnešní hodiny na zakázky";
    onCard = () => nav("/?shift=1");
  } else if (daysheet) {
    chip = st.state === "ended" ? `Hotovo · ${hoursLabel(st.hours)}` : "Mimo práci";
    big = "Denní výkaz"; sub = st.state === "ended" ? "Dnes zapsáno" : "Podrž tlačítko dole a začni den";
    onCard = () => nav("/dochazka");
  } else {
    chip = st.state === "ended" ? `Hotovo · ${hoursLabel(st.hours)}` : "Mimo práci";
    big = siteName || "Vyber stavbu";
    sub = !siteName ? "Klepni a vyber, kde dnes pracuješ"
      : st.state === "ended" ? "Dnes odpracováno — díky"
      : last ? `Poslední příchod ${dayLabel(last.date, todayIso())} ${last.from || ""}`.trim()
      : "Podrž tlačítko dole a jsi v práci";
    onCard = () => setPickOpen(true);
  }
  const hr = Math.floor((pragueMin(new Date()) ?? 600) / 60);
  const greeting = hr < 10 ? "Dobré ráno" : hr < 18 ? "Dobrý den" : "Dobrý večer";
  const dateStr = new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "long", timeZone: TZ }).format(new Date());

  const third = working && !daysheet
    ? { icon: ArrowLeftRight, label: "Jiná stavba", onClick: () => nav("/?shift=1&move=1"), testid: "qa-site" }
    : { icon: CalendarX2, label: "Volno · lékař", onClick: () => setAbsOpen(true), testid: "qa-volno" };
  // první akce podle role: dělník fotí stavbu, subdodavatel nahrává fakturu, skladník sklad
  const first = role === "SUBCONTRACTOR"
    ? { icon: ReceiptText, label: "Nahrát fakturu", onClick: () => nav("/moje-faktury?nahrat=1"), testid: "qa-invoice" }
    : role === "WAREHOUSE"
    ? { icon: Warehouse, label: "Sklad", onClick: () => nav("/sklad"), testid: "qa-warehouse" }
    : { icon: Camera, label: "Fotka ze stavby", onClick: photo, badge: photosToday || null, testid: "qa-photo" };
  const more = [
    ...(working && !daysheet ? [{ icon: CalendarX2, label: "Volno · lékař", sub: "Dovolená, lékař, nemoc…", go: () => { setMoreOpen(false); setTimeout(() => setAbsOpen(true), 0); } }] : []),
    { icon: Clock, label: "Moje docházka", sub: "Hodiny po dnech a měsících", go: () => nav("/dochazka") },
    { icon: ClipboardList, label: "Všechny úkoly", sub: "Moje i ze stavby", go: () => nav("/ukoly") },
    ...(role === "SUBCONTRACTOR" ? [{ icon: ReceiptText, label: "Moje faktury", sub: "Nahrané faktury a jejich schválení", go: () => nav("/moje-faktury") }]
      : role === "WAREHOUSE" ? [{ icon: Briefcase, label: "Zakázky", sub: "Adresy a týmy staveb", go: () => nav("/zakazky") }]
      : [{ icon: ImageIcon, label: "Galerie", sub: "Fotky z mých staveb", go: () => nav("/galerie") }]),
  ];

  return (
    <div className="-mt-1" data-testid="worker-home">
      <h1 className="mx-1 mt-1 font-heading text-[28px] font-extrabold tracking-tight truncate">{greeting}</h1>
      <p className="mx-1 mt-0.5 mb-3.5 text-sm text-muted-foreground truncate">{dateStr}{user?.company_name ? ` · ${user.company_name}` : ""}</p>

      <button type="button" onClick={onCard} data-testid="worker-pass"
        aria-label={fix ? "Doplnit konec neodhlášené směny" : working ? "Moje směna — jiná stavba nebo odchod" : daysheet ? "Moje docházka" : "Vybrat stavbu, kde dnes pracuješ"}
        className={`pp-pass ${working ? "on" : fix ? "alert" : ""} w-full rounded-[26px] px-5 pt-[18px] pb-[16px] text-left flex flex-col gap-3.5 active:scale-[.98] transition-transform motion-reduce:transition-none`}>
        <span className="flex items-center justify-between w-full gap-2">
          <span className="text-sm font-semibold text-white/80">Moje směna</span>
          {working
            ? <span className="text-xs font-bold bg-white text-[#1f6b52] rounded-xl px-2.5 py-1 inline-flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full bg-success" />V práci</span>
            : <span className="text-xs font-bold bg-white/15 rounded-xl px-2.5 py-1 whitespace-nowrap" data-testid="worker-pass-chip">{sh ? chip : "…"}</span>}
        </span>
        {working ? <Running since={st.since} label={siteName || (daysheet ? "Denní výkaz" : "V práci")} /> : (
          <>
            <span className="block min-w-0">
              <span className="flex items-center gap-1.5 min-w-0">
                <span className="font-heading text-[30px] font-extrabold leading-tight truncate" data-testid="worker-pass-site">{sh ? big : "…"}</span>
                {!daysheet && !fix && st.state !== "draft" && <ChevronDown size={22} className="shrink-0 opacity-70" aria-hidden="true" />}
              </span>
              <span className="block mt-1.5 text-sm text-white/80 truncate" data-testid="worker-pass-sub">{sh ? sub : ""}</span>
            </span>
            {fix ? (
              <span className="flex items-center justify-between gap-3" data-testid="worker-pass-fix">
                <span className="text-[13px] text-white/85 min-w-0">{fix.blocks_check_in !== false ? "Bez toho se dnes do práce nepřihlásíš." : "Doplň, kdy jsi skončil."}{fixRows.length > 1 ? ` Čeká ještě ${fixRows.length - 1} ${fixRows.length - 1 === 1 ? "den" : fixRows.length - 1 < 5 ? "dny" : "dní"}.` : ""}</span>
                <span className="shrink-0 h-10 px-4 rounded-full bg-white text-[#a8281c] text-[14px] font-bold grid place-items-center">Doplnit</span>
              </span>
            ) : <Bar label="Tento týden" value={`${String(week).replace(".", ",")} / 40 h`} pct={Math.min(100, (week / 40) * 100)} color="hsl(var(--accent-orange))" />}
          </>
        )}
      </button>

      {!fix && fixRows.slice(-2).map((r) => (
        <button key={r.id} type="button" onClick={() => setFixRow(r)} data-testid={`worker-fix-${r.id}`}
          className="mt-3 w-full rounded-[18px] bg-destructive/10 px-3.5 py-3 flex items-center gap-3 text-left active:scale-[.98] transition-transform motion-reduce:transition-none">
          <AlertTriangle size={18} className="text-destructive shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-bold truncate">Nebyl jsi odhlášen {czDay(r.check_in)}</span>
            <span className="block text-[12.5px] text-muted-foreground truncate">{r.project} · doplň, kdy jsi skončil</span>
          </span>
          <span className="shrink-0 h-9 px-3.5 rounded-full bg-destructive text-white text-[13px] font-bold grid place-items-center">Doplnit</span>
        </button>
      ))}

      <div role="group" aria-label="Rychlé akce" className="grid grid-cols-4 gap-1 mt-4">
        <QuickAction {...first} />
        <QuickAction icon={ClipboardPlus} label="Nový úkol" onClick={() => setTaskOpen(true)} testid="qa-task" />
        <QuickAction {...third} />
        <QuickAction icon={MoreHorizontal} label="Další" onClick={() => setMoreOpen(true)} testid="qa-more" />
      </div>

      <div className="mt-5 space-y-4">
        <MyShiftBlock mode={mode} hideCard onState={onShiftState} />
        <MyAdvancesCard />
      </div>

      {photoPick.inputs}
      <ShiftEndFixDialog row={fixRow} open={!!fixRow} onOpenChange={(o) => { if (!o) setFixRow(null); }} onSaved={loadFix} />
      <SitePicker open={pickOpen} onOpenChange={setPickOpen} projects={projects} selectedId={site} onPick={chooseSite} />
      <NewTaskDialog open={taskOpen} onOpenChange={setTaskOpen} hideTrigger defaultProjectId={sitePid || undefined}
        onCreated={() => { setTaskOpen(false); window.dispatchEvent(new Event("pp-shift-changed")); }} />
      <AbsenceDialog open={absOpen} onClose={() => setAbsOpen(false)} mode="request" initial={{ date_from: todayIso(), date_to: todayIso() }} onSaved={() => setAbsOpen(false)} />
      <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
        <DialogContent sheet className="max-w-md" data-testid="worker-more">
          <DialogTitle className="font-heading text-[21px] font-bold">Další</DialogTitle>
          <div className="rounded-[20px] overflow-hidden bg-background">
            {more.map((m, i) => (
              <button key={m.label} type="button" onClick={m.go} className={`w-full min-h-[58px] flex items-center gap-3 px-3.5 py-2 text-left ${i ? "border-t border-border" : ""}`}>
                <span className="w-9 h-9 rounded-xl bg-secondary grid place-items-center shrink-0"><m.icon size={18} aria-hidden="true" /></span>
                <span className="flex-1 min-w-0"><span className="block text-base font-semibold">{m.label}</span><span className="block text-[13px] text-muted-foreground truncate">{m.sub}</span></span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
